/**
 * Search-engine metadata for the public pages, in both languages. The API writes it into
 * the HTML of the first page load (what crawlers and link previews see); the web app sets
 * the same title while navigating. One source, so they never disagree.
 *
 * Language-specific URLs use `?lang=da` / `?lang=en`; the URL without it is the
 * x-default and follows the visitor's browser language.
 */
import type { Locale } from "./index.js";
import { SITE } from "./site.js";

export type SeoPageId =
  | "landing"
  | "directory"
  | "signup"
  | "login"
  | "privacy"
  | "terms"
  | "dpa"
  | "cookies"
  | "book"
  | "private"
  | "notFound";

/** Paths of the indexable static pages (booking pages are added per clinic). */
export const SEO_STATIC_PATHS = {
  landing: "/",
  directory: "/clinics",
  signup: "/signup",
  privacy: "/privacy",
  terms: "/terms",
  dpa: "/dpa",
  cookies: "/cookies",
} as const satisfies Partial<Record<SeoPageId, string>>;

export interface SeoMatch {
  page: SeoPageId;
  /** False: noindex. Accounts, tokens and the app itself never appear in search results. */
  indexable: boolean;
  /** Clinic slug for /book/:slug. */
  slug?: string;
}

const LEGAL_PAGES = new Set<SeoPageId>(["privacy", "terms", "dpa", "cookies"]);
export const isLegalPage = (page: SeoPageId) => LEGAL_PAGES.has(page);

/** Which page a path is. Mirrors the routes in apps/web/src/main.tsx. */
export function matchSeoPage(pathname: string): SeoMatch {
  const path = pathname.length > 1 ? pathname.replace(/\/+$/, "") : pathname;
  for (const [page, p] of Object.entries(SEO_STATIC_PATHS)) {
    if (path === p) return { page: page as SeoPageId, indexable: true };
  }
  if (path === "/login") return { page: "login", indexable: false };
  const book = /^\/book\/([a-z0-9-]{1,64})$/.exec(path);
  if (book) return { page: "book", indexable: true, slug: book[1] };
  if (
    /^\/(app|invite|reset-password|forgot-password)(\/|$)/.test(path) ||
    /^\/book\/[a-z0-9-]{1,64}\/manage\//.test(path)
  ) {
    return { page: "private", indexable: false };
  }
  return { page: "notFound", indexable: false };
}

interface PageText {
  title: string;
  description: string;
}

type Texts = Record<Exclude<SeoPageId, "book">, PageText> & {
  book: (clinic: { name: string; address: string; description: string }) => PageText;
};

const n = SITE.name;

export const SEO_TEXT: Record<Locale, Texts> = {
  en: {
    landing: {
      title: `${n}: online booking system for clinics`,
      description:
        "Online booking for physiotherapists, chiropractors, massage therapists and other clinics. Patients book 24/7, with automatic reminders. EU-hosted and GDPR-ready.",
    },
    directory: {
      title: `Find a clinic and book online | ${n}`,
      description:
        "Find physiotherapists, chiropractors, massage therapists and other clinics that take online bookings.",
    },
    signup: {
      title: `Set up your clinic | ${n}`,
      description:
        "Create your clinic's online booking page in a few minutes: services, practitioners and opening hours.",
    },
    login: { title: `Sign in | ${n}`, description: `Sign in to ${n}.` },
    privacy: {
      title: `Privacy policy | ${n}`,
      description: `How ${n} handles personal data for clinics, their patients and visitors, and your rights under GDPR.`,
    },
    terms: {
      title: `Terms of service | ${n}`,
      description: `The terms for clinics using ${n} for online booking.`,
    },
    dpa: {
      title: `Data processing agreement | ${n}`,
      description: `The GDPR article 28 data processing agreement between ${n} and the clinics that use it.`,
    },
    cookies: {
      title: `Cookie policy | ${n}`,
      description: `${n} uses only strictly necessary cookies: no tracking, no advertising, no consent banner.`,
    },
    book: (c) => ({
      title: `Book an appointment at ${c.name} | ${n}`,
      description: clip(
        c.description ||
          `Book an appointment at ${c.name}${c.address ? `, ${c.address}` : ""} online. See free times and book in under a minute.`,
      ),
    }),
    private: { title: n, description: "" },
    notFound: { title: `Page not found | ${n}`, description: "" },
  },
  da: {
    landing: {
      title: `${n}: online bookingsystem til klinikker`,
      description:
        "Online booking til fysioterapeuter, kiropraktorer, massører og andre klinikker. Patienterne booker døgnet rundt med automatiske påmindelser. Hostet i EU og klar til GDPR.",
    },
    directory: {
      title: `Find en klinik og book online | ${n}`,
      description: "Find fysioterapeuter, kiropraktorer, massører og andre klinikker, der tager imod online booking.",
    },
    signup: {
      title: `Opret din klinik | ${n}`,
      description: "Opret din kliniks online bookingside på få minutter: ydelser, behandlere og åbningstider.",
    },
    login: { title: `Log ind | ${n}`, description: `Log ind på ${n}.` },
    privacy: {
      title: `Privatlivspolitik | ${n}`,
      description: `Sådan behandler ${n} personoplysninger om klinikker, deres patienter og besøgende, og dine rettigheder efter GDPR.`,
    },
    terms: {
      title: `Vilkår og betingelser | ${n}`,
      description: `Betingelserne for klinikker, der bruger ${n} til online booking.`,
    },
    dpa: {
      title: `Databehandleraftale | ${n}`,
      description: `Databehandleraftalen efter GDPR artikel 28 mellem ${n} og de klinikker, der bruger systemet.`,
    },
    cookies: {
      title: `Cookiepolitik | ${n}`,
      description: `${n} bruger kun strengt nødvendige cookies: ingen sporing, ingen reklamer og intet cookiebanner.`,
    },
    book: (c) => ({
      title: `Book tid hos ${c.name} | ${n}`,
      description: clip(
        c.description ||
          `Book tid hos ${c.name}${c.address ? `, ${c.address}` : ""} online. Se ledige tider og book på under et minut.`,
      ),
    }),
    private: { title: n, description: "" },
    notFound: { title: `Siden findes ikke | ${n}`, description: "" },
  },
};

/** Meta descriptions are cut at about 155 characters in results. */
function clip(text: string, max = 155): string {
  const t = text.replace(/\s+/g, " ").trim();
  return t.length <= max ? t : `${t.slice(0, max - 1).replace(/\s+\S*$/, "")}…`;
}
